import * as Either from 'fp-ts/Either';
import * as Option from 'fp-ts/Option';
import { flap as flapInternal } from 'fp-ts/Functor';
import { apFirst as apFirstInternal, apSecond as apSecondInternal, } from 'fp-ts/Apply';
import { chainFirst as chainFirstInternal } from 'fp-ts/Chain';
import { pipe, flow, identity, constNull, constVoid, } from 'fp-ts/function';
import isUndefined from 'lodash-es/isUndefined';
import { ErrorAsync, eqErrorAsync, unknown, server, client, decoding, } from './ErrorAsync';
import { decodeWith } from './Decoding';
/**
 * @deprecated in favour of Query
 */
export const URI = 'RemoteData';
/**
 * Creates an `Idle` RemoteData.
 *
 * @deprecated in favour of Query
 */
export const idle = { tag: 'idle' };
/**
 * Creates a `Loading` RemoteData.
 *
 * @deprecated in favour of Query
 */
export const loading = (data) => ({
    tag: 'loading',
    data,
});
/**
 * A `Loading` RemoteData.
 *
 * This is to be used where creating a new RemoteData loading object
 * is not recommended as objects can change across rerenders in react
 *
 * @example
 *
 * ```ts
 * const [rd, setRd] = useRemoteData(
 * fetcher,
 *  'notify_context',
 *  RemoteData.initialLoading,
 * );
 * ```
 *
 * In the example above, we are setting the default value of the remote data to loading with the `useRemoteData` hook.
 * If we were to use `RemoteData.loading()` in place of `RemoteData.initialLoading` would mean the identity of the
 * Remote Data object would change if the component the hook is been used re-renders which could lead to an infinite loop
 *
 * @deprecated in favour of Query
 */
export const initialLoading = {
    tag: 'loading',
    data: undefined,
};
/**
 * Creates a `Failure` RemoteData.
 *
 * @deprecated in favour of Query
 */
export const failure = (error) => ({
    tag: 'failure',
    error,
});
/**
 * Creates a `Failure` RemoteData with an `ErrorAsync.Unknown` error.
 *
 * @deprecated in favour of Query
 */
export const failureUnknown = flow(unknown, failure);
/**
 * Creates a `Failure` RemoteData with an `ErrorAsync.Decoding` error.
 *
 * @deprecated in favour of Query
 */
export const failureDecoding = (value) => flow(decoding(value), failure);
/**
 * Creates a `Failure` RemoteData with an `ErrorAsync.Client` error.
 *
 * @deprecated in favour of Query
 */
export const failureClient = flow(client, failure);
/**
 * Creates a `Failure` RemoteData with an `ErrorAsync.Server` error.
 *
 * @deprecated in favour of Query
 */
export const failureServer = flow(server, failure);
/**
 * Creates a `Success` RemoteData.
 *
 * @deprecated in favour of Query
 */
export const success = (data) => ({
    tag: 'success',
    data,
});
// -----------------------------------------------------------------------------
// guards
// -----------------------------------------------------------------------------
/**
 * @deprecated in favour of Query
 */
export const isIdle = (rd) => rd.tag === 'idle';
/**
 * @deprecated in favour of Query
 */
export const isLoading = (rd) => rd.tag === 'loading';
/**
 * @deprecated in favour of Query
 */
export const isSuccess = (rd) => rd.tag === 'success';
/**
 * @deprecated in favour of Query
 */
export const isFailure = (rd) => rd.tag === 'failure';
// -----------------------------------------------------------------------------
// destructors
// -----------------------------------------------------------------------------
/**
 * Extract a value from RemoteData by pattern matching on the different cases.
 *
 * ```tsx
 * import { ErrorAsync } from '@catawiki/runtime-toolkit';
 *
 * type RenderUser = (user: RemoteData<User>) => ReactNode;
 *
 * const renderUser: RenderUser = match({
 *   idle: () => <IdleMessage />,
 *   loading: () => <Loading />,
 *   success: user => <UserCard {...user} />,
 *   failure: error => <ErrorMessage error={error} />,
 * });
 *
 * renderUser(idle);
 * renderUser(loading);
 * renderUser(success({ id: 1234, name: 'Jane Doe' }));
 * renderUser(failure(ErrorAsync.client('Not found', 404, {})));
 * ```
 *
 * @deprecated in favour of Query
 */
export const match = (matcher) => (rd) => {
    if (isIdle(rd)) {
        return matcher.idle();
    }
    if (isLoading(rd)) {
        return matcher.loading(rd.data);
    }
    if (isSuccess(rd)) {
        return matcher.success(rd.data);
    }
    return matcher.failure(rd.error);
};
/**
 * A helper match function that runs a callback when the RemoteData is in Success or Failure state
 *
 * @deprecated in favour of Query
 */
export const matchFinally = (onFailure, onSuccess) => (rd) => pipe(rd, match({
    idle: constVoid,
    loading: constVoid,
    success: onSuccess,
    failure: onFailure,
}));
/**
 * A helper match function that runs a callback when the RemoteData is in Success state
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const onSuccess = (data: string) => {
 *  assert.strictEqual(data, 'foo')
 * }
 *
 * pipe(
 *  RemoteData.success('foo'),
 *  RemoteData.matchSuccess(onSuccess)
 * )
 *
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const onSuccess = (data: string) => {
 *  throw new Error('should not be called')
 * }
 *
 * pipe(
 *  RemoteData.failure(ErrorAsync.unknown(new Error('error'))),
 *  RemoteData.matchSuccess(onSuccess)
 * )
 *
 * @deprecated in favour of Query
 */
export const matchSuccess = (onSuccess) => (rd) => {
    pipe(rd, matchFinally(constVoid, onSuccess));
};
/**
 * A helper match function that runs a callback when the RemoteData is in Failure state
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const onFailure = (data: string) => {
 *  assert.strictEqual(data, ErrorAsync.unknown(new Error('error')))
 * }
 *
 * pipe(
 *  RemoteData.failure(ErrorAsync.unknown(new Error('error'))),
 *  RemoteData.matchFailure(onFailure)
 * )
 *
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const onSuccess = (data: string) => {
 *  throw new Error('should not be called')
 * }
 *
 * pipe(
 *  RemoteData.success('foo'),
 *  RemoteData.matchFailure(onSuccess)
 * )
 *
 * @deprecated in favour of Query
 */
export const matchFailure = (onFailure) => (rd) => {
    pipe(rd, matchFinally(onFailure, constVoid));
};
/**
 * Transforms a `RemoteData<A>` to a nullable value (`A | null`), returning `A` on the Success or Loading case and `null` otherwise.
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * assert.strictEqual(RemoteData.toNullable(RemoteData.idle), null)
 * assert.strictEqual(RemoteData.toNullable(RemoteData.loading('123')), '123')
 * assert.strictEqual(RemoteData.toNullable(RemoteData.success('123')), '123')
 * assert.strictEqual(RemoteData.toNullable(RemoteData.failure(ErrorAsync.unknown(new Error('error')))), null)
 *
 * @deprecated in favour of Query
 */
export const toNullable = (remoteDataA) => match({
    idle: constNull,
    loading: (data) => data ?? null,
    success: identity,
    failure: constNull,
})(remoteDataA);
// -------------------------------------------------------------------------------------
// non-pipeables (internals)
// -------------------------------------------------------------------------------------
const mapInternal = (remoteDataA, f) => match({
    idle: () => idle,
    loading: (data) => (isUndefined(data) ? loading() : loading(f(data))),
    success: (data) => success(f(data)),
    failure,
})(remoteDataA);
const apInternal = (rdf, rda) => match({
    idle: () => idle,
    loading: (a) => isUndefined(a) ? loading() : mapInternal(rdf, (f) => f(a)),
    success: (a) => mapInternal(rdf, (f) => f(a)),
    failure,
})(rda);
const chainInternal = (rda, f) => match({
    idle: () => idle,
    loading: (a) => (isUndefined(a) ? loading() : f(a)),
    success: (a) => f(a),
    failure,
})(rda);
const prependDecodingErrors = (err) => (decodingError) => ({
    ...decodingError,
    errors: [err, ...decodingError.errors],
});
/**
 * @deprecated in favour of Query
 */
export const throwError = (e) => pipe(e, decodeWith(ErrorAsync), Either.mapLeft(prependDecodingErrors('throwError: failed to decode into ErrorAsync')), 
// We go from (any) E to ErrorAsync
// Left  -> failed to decode E into ErrorAsync
// Right -> E can be decoded into ErrorAsync
Either.match(failure, failure));
// -----------------------------------------------------------------------------
// constructors
// -----------------------------------------------------------------------------
/**
 * @deprecated in favour of Query
 */
export const fromOption = (onNone) => (mbA) => pipe(mbA, Option.match(() => throwError(onNone()), success));
/**
 * a `RemoteData` from an `Either`
 *
 * @example
 *
 * ```ts
 * import { RemoteData, ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 *
 * const error = ErrorAsync.decoding(null)(['missing x on y'])
 *
 * assert.deepStrictEqual(RemoteData.fromEither(Either.right('foo')), RemoteData.success('foo'))
 * assert.deepStrictEqual(RemoteData.fromEither(Either.left(error)), RemoteData.failure(error))
 *
 * ```
 *
 * @deprecated in favour of Query
 */
export const fromEither = (ea) => pipe(ea, Either.match(throwError, success));
/**
 * a `RemoteData` from a `Promise`
 *
 * @example
 *
 * ```ts
 * import { RemoteData, ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * const error = ErrorAsync.decoding(null)(['missing x on y'])
 *
 * assert.deepStrictEqual(await RemoteData.fromPromise(Promise.resolve('foo')), RemoteData.success('foo'))
 * assert.deepStrictEqual(await RemoteData.fromPromise(Promise.reject(error)), RemoteData.failure(error))
 *
 * ```
 *
 * @deprecated in favour of Query
 */
export const fromPredicate = (predicate, onFalse) => (a) => predicate(a) ? success(a) : throwError(onFalse(a));
// -----------------------------------------------------------------------------
// combinators
// -----------------------------------------------------------------------------
/**
 * filter a `RemoteData` with a predicate
 *
 * @example
 *
 * ```ts
 * import { RemoteData, ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * const error = ErrorAsync.decoding(null)(['missing x on y'])
 *
 * assert.deepStrictEqual(RemoteData.filter((s: string) => s === 'foo')(RemoteData.success('foo')), RemoteData.success('foo'))
 * assert.deepStrictEqual(RemoteData.filter((s: string) => s === 'foo')(RemoteData.success('bar')), RemoteData.failure(error))
 *
 * ```
 *
 * @deprecated in favour of Query
 */
export const filterOrElse = (predicate, onFalse) => (rda) => chainInternal(rda, (a) => {
    if (predicate(a)) {
        if (isLoading(rda)) {
            return loading(a);
        }
        return success(a);
    }
    return throwError(onFalse(a));
});
/**
 * Transforms a `RemoteData<A>` to a `Option<A>`.
 *
 * @example
 * import { some, none } from 'fp-ts/Option'
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const error = ErrorAsync.decoding(null)(['missing x on y'])
 *
 * assert.deepStrictEqual(RemoteData.toOption(RemoteData.success('foo')), some('foo'))
 * assert.deepStrictEqual(RemoteData.toOption(RemoteData.idle), none)
 * assert.deepStrictEqual(RemoteData.toOption(RemoteData.loading), none)
 * assert.deepStrictEqual(RemoteData.toOption(RemoteData.failure(error)), none)
 *
 * @deprecated in favour of Query
 */
export const toOption = flow(toNullable, Option.fromNullable);
/**
 * Transforms a `RemoteData<A>` to a `Either<E, A>`.
 *
 * @example
 * import { right, left } from 'fp-ts/Either'
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const error = ErrorAsync.decoding(null)(['missing x on y'])
 *
 * const toError = RemoteData.toEither<'idle' | 'loading' | 'failure'>(
 *   (status) => status,
 *   (_failure) => 'failure'
 * )
 *
 * assert.deepStrictEqual(toError(RemoteData.success('foo')), right('foo'))
 * assert.deepStrictEqual(toError(RemoteData.idle), left('idle'))
 * assert.deepStrictEqual(toError(RemoteData.loading), left('loading'))
 * assert.deepStrictEqual(toError(RemoteData.failure(error)), left('failure'))
 *
 * @deprecated in favour of Query
 */
export const toEither = (onNone, onFailure) => (remoteDataA) => match({
    idle: () => Either.left(onNone('idle')),
    loading: () => Either.left(onNone('loading')),
    success: Either.right,
    failure: flow(Either.left, Either.mapLeft(onFailure)),
})(remoteDataA);
/**
 * Allows to transform the failure branch into a success one
 *
 * @deprecated in favour of Query
 */
export const mapFailureToSuccess = (f) => (remoteDataA) => match({
    idle: () => idle,
    loading,
    success,
    failure: (error) => success(f(error)),
})(remoteDataA);
// -----------------------------------------------------------------------------
// instances
// -----------------------------------------------------------------------------
/**
 * Get an [fp-ts Eq](https://gcanti.github.io/fp-ts/modules/Eq.ts.html) instance.
 *
 * @example
 * import { RemoteData, ErrorAsync } from '@catawiki/runtime-toolkit'
 * import { Eq as eqNumber } from 'fp-ts/number'
 *
 * const eq = RemoteData.getEq(eqNumber)
 * assert.strictEqual(eq.equals(RemoteData.idle, RemoteData.idle), true)
 * assert.strictEqual(eq.equals(RemoteData.loading, RemoteData.success(1)), false)
 * assert.strictEqual(eq.equals(RemoteData.failure(ErrorAsync.decoding(null)(['no x'])), RemoteData.failure(ErrorAsync.decoding(null)(['no x']))), true)
 * assert.strictEqual(eq.equals(RemoteData.failure(ErrorAsync.decoding(null)([])), RemoteData.failure(ErrorAsync.client('Not found', 404, {}))), false)
 * assert.strictEqual(eq.equals(RemoteData.success(1), RemoteData.idle), false)
 * assert.strictEqual(eq.equals(RemoteData.success(1), RemoteData.success(2)), false)
 * assert.strictEqual(eq.equals(RemoteData.success(1), RemoteData.success(1)), true)
 *
 * @deprecated in favour of Query
 */
export function getEq(E) {
    return {
        equals: (x, y) => {
            if (x === y) {
                return true;
            }
            if (isIdle(x)) {
                return isIdle(y);
            }
            if (isLoading(x)) {
                return isLoading(y);
            }
            if (isFailure(x)) {
                return isFailure(y) && eqErrorAsync.equals(x.error, y.error);
            }
            if (isSuccess(x)) {
                return isSuccess(y) && E.equals(x.data, y.data);
            }
            return false;
        },
    };
}
// -----------------------------------------------------------------------------
// type class members
// -----------------------------------------------------------------------------
/**
 * @deprecated in favour of Query
 */
export const Functor = { URI, map: mapInternal };
/**
 * Map allows to transform the data inside RemoteData
 *
 * ```ts
 * type map = (A => B) => RemoteData<A> => RemoteData<B>
 * ```
 *
 * Only transforms the inner data when on the success branch, otherwise returns the RemoteData unchanged.
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const len = (s: string) => s.length
 *
 * assert.deepStrictEqual(RemoteData.map(len)(RemoteData.success('foo')), RemoteData.success(3))
 * assert.deepStrictEqual(RemoteData.map(len)(RemoteData.idle), RemoteData.idle)
 * assert.deepStrictEqual(RemoteData.map(len)(RemoteData.loading), RemoteData.loading)
 * assert.deepStrictEqual(RemoteData.map(len)(RemoteData.failure(ErrorAsync.unknown(['foo']))), RemoteData.failure(ErrorAsync.unknown(['foo'])))
 *
 * @deprecated in favour of Query
 */
export const map = (f) => (rdA) => mapInternal(rdA, f);
/**
 * Derived from `Functor`
 *
 * @deprecated in favour of Query
 */
export const flap = flapInternal(Functor);
/**
 * @deprecated in favour of Query
 */
export const Apply = { URI, map: mapInternal, ap: apInternal };
/**
 * @deprecated in favour of Query
 */
export const ap = (rdf) => (rda) => apInternal(rdf, rda);
/**
 * Given two RemoteData values (remoteDataA & remoteDataB): Discard the 1st and return the 2nd (as long as the 1st is a Success).
 *
 * ```
 * remoteDataA == Success -> return remoteDataB
 * remoteDataA != Success -> return remoteDataA
 * ```
 *
 * `apFirst` is meant to be used with `pipe`, which could be seen as a replacement for methods. So
 *
 * ```ts
 * pipe(
 *   remoteDataA,
 *   apFirst(remoteDataB)
 * )
 * ```
 *
 * would be something like
 *
 * ```ts
 * remoteDataA.apFirst(remoteDataB)
 * ```
 *
 * @deprecated in favour of Query
 */
export const apFirst = apFirstInternal(Apply);
/**
 * Given two RemoteData values (remoteDataA & remoteDataB): Discards the 2nd and returns the 1st (as long as the 2nd is a Success).
 *
 * ```
 * remoteDataB == Success -> return remoteDataA
 * remoteDataB != Success -> return remoteDataB
 * ```
 *
 * `apSecond` is meant to be used with `pipe`, which could be seen as a replacement for methods. So
 *
 * ```ts
 * pipe(
 *   remoteDataA,
 *   apSecond(remoteDataB)
 * )
 * ```
 *
 * would be something like
 *
 * ```ts
 * remoteDataA.apSecond(remoteDataB)
 * ```
 *
 * @deprecated in favour of Query
 */
export const apSecond = apSecondInternal(Apply);
/**
 * Creates a `Success` RemoteData.
 *
 * @deprecated in favour of Query
 */
export const of = success;
/**
 * @deprecated in favour of Query
 */
export const Applicative = {
    URI,
    map: mapInternal,
    ap: apInternal,
    of,
};
/**
 * @deprecated in favour of Query
 */
export const Chain = {
    URI,
    map: mapInternal,
    ap: apInternal,
    chain: chainInternal,
};
/**
 * Chain allows to transform a RemoteData value by using the data inside it:
 *
 * ```ts
 * type chain = (A => RemoteData<B>) => RemoteData<A> => RemoteData<B>
 * ```
 *
 * The transformation is only applied when on the success branch, otherwise returns the RemoteData unchanged.
 *
 * It is similar to map but the chaining function returns `RemoteData<B>` instead of `B`. This allows to change not only the data contained in the case of `Success` but also to change to another branch.
 *
 * @example
 * import { RemoteData, ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * const error = ErrorAsync.decoding(null)(['missing x on y'])
 *
 * const f = (s: string) => s === 'foo' ? RemoteData.loading : RemoteData.idle
 *
 * assert.deepStrictEqual(f('foo'), RemoteData.loading)
 * assert.deepStrictEqual(f('bar'), RemoteData.idle)
 *
 * assert.deepStrictEqual(RemoteData.chain(f)(RemoteData.success('foo')), RemoteData.loading)
 * assert.deepStrictEqual(RemoteData.chain(f)(RemoteData.success('bar')), RemoteData.idle)
 * assert.deepStrictEqual(RemoteData.chain(f)(RemoteData.idle), RemoteData.idle)
 * assert.deepStrictEqual(RemoteData.chain(f)(RemoteData.loading), RemoteData.loading)
 * assert.deepStrictEqual(RemoteData.chain(f)(RemoteData.failure(error)), RemoteData.failure(error))
 *
 * @deprecated in favour of Query
 */
export const chain = (f) => (rda) => chainInternal(rda, f);
/**
 * @deprecated in favour of Query
 */
export const flatten = chain(identity);
/**
 *
 * @deprecated in favour of Query
 */
export const chainFirst = chainFirstInternal(Chain);
/**
 * @deprecated in favour of Query
 */
export const Monad = {
    URI,
    map: mapInternal,
    ap: apInternal,
    of,
    chain: chainInternal,
};
/**
 * @deprecated in favour of Query
 */
export const MonadThrow = {
    URI,
    map: mapInternal,
    ap: apInternal,
    of,
    chain: chainInternal,
    throwError,
};
/**
 * The [fp-ts typeclasses](https://paulgray.net/typeclasses-in-typescript/) implemented by RemoteData.
 *
 * - [Functor](https://gcanti.github.io/fp-ts/modules/Functor.ts.html)
 * - [Applicative](https://gcanti.github.io/fp-ts/modules/Applicative.ts.html) / [Apply](https://gcanti.github.io/fp-ts/modules/Apply.ts.html)
 * - [Monad](https://gcanti.github.io/fp-ts/modules/Monad.ts.html) / [Chain](https://gcanti.github.io/fp-ts/modules/Chain.ts.html)
 * - [MonadThrow](https://gcanti.github.io/fp-ts/modules/MonadThrow.ts.html)
 *
 * @deprecated in favour of Query
 */
export const remoteData = {
    URI,
    map: mapInternal,
    ap: apInternal,
    of: success,
    chain: chainInternal,
    throwError,
};
// -----------------------------------------------------------------------------
// utils
// -----------------------------------------------------------------------------
/**
 * On `Success` it returns `true` if the wrapped data satisfies the predicate, returns `false` otherwise.
 *
 * @deprecated in favour of Query
 *
 * @example
 * import { ErrorAsync, RemoteData } from '@catawiki/runtime-toolkit'
 *
 * const isOne = (n: number) => n === 1
 *
 * assert.strictEqual(RemoteData.exists(isOne)(RemoteData.idle), false)
 * assert.strictEqual(RemoteData.exists(isOne)(RemoteData.loading), false)
 * assert.strictEqual(RemoteData.exists(isOne)(RemoteData.success(2)), false)
 * assert.strictEqual(RemoteData.exists(isOne)(RemoteData.success(1)), true)
 * assert.strictEqual(RemoteData.exists(isOne)(RemoteData.failure(ErrorAsync.unknown('some error'))), false)
 */
export const exists = (f) => match({
    idle: () => false,
    loading: () => false,
    success: f,
    failure: () => false,
});
/**
 * On `Failure` it returns `true` if the wrapped error satisfies the predicate, returns `false` otherwise.
 *
 * @deprecated in favour of Query
 *
 * @example
 * import { ErrorAsync, RemoteData} from '@catawiki/runtime-toolkit'
 *
 * const is404 = ErrorAsync.isOfStatus(404);
 *
 * assert.strictEqual(RemoteData.failureExists(is404)(RemoteData.idle), false)
 * assert.strictEqual(RemoteData.failureExists(is404)(RemoteData.loading), false)
 * assert.strictEqual(RemoteData.failureExists(is404)(RemoteData.success(1)), false)
 * assert.strictEqual(RemoteData.failureExists(is404)(RemoteData.failure(ErrorAsync.unknown('some error'))), false)
 * assert.strictEqual(RemoteData.failureExists(is404)(RemoteData.failure(ErrorAsync.client('Unauthorized', 401, {}))), false)
 * assert.strictEqual(RemoteData.failureExists(is404)(RemoteData.failure(ErrorAsync.client('Not found', 404, {}))), true)
 */
export const failureExists = (f) => match({
    idle: () => false,
    loading: () => false,
    success: () => false,
    failure: f,
});
